import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    Radio,
    RadioGroup,
    TextField
} from "@mui/material";
import { Form, Formik } from "formik";
import { DatePicker } from '@mui/x-date-pickers';
import useAuth from '../../context/AuthProvider';
import moment from 'moment';
import { formatUtcToLocal } from '../../helper/utils';
import { StateReportModel } from '../../models/StateReportModel';
import { FormLabel } from 'react-bootstrap';

interface Iprops {
    handleClose: () => void;
    open: boolean;
    currentStatus?: string;
    handleSubmit: (data: {
        status: string,
        noteType: string,
        noteDetail: string,
        noteUser: string,
        firstRequestDate: string,
        secondRequestDate: string,
        thirdRequestDate: string,
        suspendedDate: string,
        noteDate?: string,
        policySubStatus: string,
        dateDeclarationFiled: string,
        dateFinalReportSentToClient: string,
        taxAmount: string,
        largeReport: boolean,
        largeReportFee: string
    }) => void;
    selectedRows?: StateReportModel[] | null;
    /** Schedule mapping ActionType; when `no_action`, use the Filed Notes layout. */
    mappingActionType?: string;
}

// Pairs [stateName, name] that should set openFiledNotes true when matched
const FILED_NOTES_STATE_NAME_PAIRS: [string, string][] = [
    ['CO', 'Monthly Report'],
    ['KY', 'Annual Municipal Reconciliation (Electronic)'],
    ['KY', 'Annual Municipal Reconciliation (Individual Municipalities)'],
    ['MD', 'Quarterly Surplus Lines Tax Report'],
    ['MO', 'Quarterly Tax Report (Appendix 1)'],
    ['MO', 'Annual Tax Report (Appendix 3)'],
    ['MS', 'Monthly Certification Report'],
    ['PA', 'Monthly 1620 Report'],
];

const StateReportFilerNotes = ({ open, handleClose, handleSubmit, selectedRows = [], currentStatus, mappingActionType }: Iprops) => {
    const { user } = useAuth();
    // Status being applied (chosen from the Status menu), NOT the row's existing status.
    // Layout + Submit validation must key off the target status, otherwise changing a
    // completed row (e.g. Filed) to Waiting on Client / No Report Required validates
    // against the old status and the Submit button can never enable.
    const targetStatus = (currentStatus && currentStatus.trim())
        ? currentStatus.trim()
        : (selectedRows?.[0]?.status ?? '');

    // Filed Notes layout: schedule mapping no_action, or legacy state/name pairs.
    const [openFiledNotes, setOpenFiledNotes] = useState(false);
    useEffect(() => {
        const row = selectedRows?.[0];
        if (!row) {
            setOpenFiledNotes(false);
            return;
        }
        const isNoActionMapping =
            String(mappingActionType ?? '').trim().toLowerCase() === 'no_action';
        const rowStateName = (row as any).stateName ?? '';
        const rowName = (row as any).name ?? '';
        const matchesPair = FILED_NOTES_STATE_NAME_PAIRS.some(([s, n]) => s === rowStateName && n === rowName);
        setOpenFiledNotes(isNoActionMapping || matchesPair);
    }, [mappingActionType, selectedRows]);

    // State variables
    const [noteDetail, setNoteDetail] = useState<string>('');
    const [noteDetailFiled, setNoteDetailFiled] = useState<string>('');
    const [taxAmount, setTaxAmount] = useState<string>('');
    const [largeReport, setLargeReport] = useState(false);
    const [largeReportFee, setLargeReportFee] = useState<string>('');
    const [noteDate, setNoteDate] = useState<string>('');
    const [firstRequestDate, setFirstRequestDate] = useState('');
    // Reset form state when dialog opens for a new transaction
    useEffect(() => {
        if (open) {
            setLargeReportFee('');
            setLargeReport(false);
        }
    }, [open]);
    const [secondRequestDate, setSecondRequestDate] = useState('');
    const [thirdRequestDate, setThirdRequestDate] = useState('');
    const [suspendedDate, setSuspendedDate] = useState('');
    const [showNoteDateField, setShowNoteDateField] = useState(false);
    const [dateComplete, setDateComplete] = useState('');
    const [dateCompleteError, setDateCompleteError] = useState('');

    const initialValuesNote = { noteOption: 'Select One', noteDetail: '' };

    // Utility functions
    const shouldDisableDate = (date: Date) => {
        const minDate = new Date('1900-01-01');
        const maxDate = new Date();
        return date < minDate || date > maxDate;
    };

    const handleNoteDetailChangeFiled = (event: any) => {
        const update = event.target.value;
        setNoteDetailFiled(update);
        setTaxAmount(update);
    };

    const handleNote = (event: any) => {
        const update = event.target.value;
        setNoteDetail(update);
    };

    const handleNoteDetailChange = (event: any) => {
        const update = event.target.value;
        setNoteDetail(update);
        setShowNoteDateField(update !== '');
        setNoteDate(update === '' ? formatUtcToLocal(new Date().toISOString(), 'MM/DD/YYYY') : noteDate);
    };

    const handleNoteDate = (event: any) => {
        setNoteDate(event);
    };

    // Date handlers
    const handleDateComplete = (val: string | undefined) => {
        if (!val) {
            setDateComplete('');
            setDateCompleteError('');
            return;
        }

        const formattedInputDate = formatUtcToLocal(val, "MM/DD/YYYY");
        const todayInUiTimezone = formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY");

        if (formattedInputDate === "N/A") {
            setDateComplete('');
            setDateCompleteError('Enter date in MM/DD/YYYY format');
            return;
        }

        const inputDate = moment(formattedInputDate, "MM/DD/YYYY", true);
        const todayDate = moment(todayInUiTimezone, "MM/DD/YYYY", true);
        if (!inputDate.isValid() || !todayDate.isValid()) {
            setDateComplete('');
            setDateCompleteError('Enter date in MM/DD/YYYY format');
            return;
        }

        if (inputDate.isAfter(todayDate, 'day')) {
            setDateComplete('');
            setDateCompleteError('Please enter a date up to today');
            return;
        }

        setDateComplete(formattedInputDate);
        setDateCompleteError('');
    };

    const handleFirstRequestChange = (val: string | undefined) => {
        if (val != null) {
            setFirstRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleSecondRequestChange = (val: string | undefined) => {
        if (val != null) {
            setSecondRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleThirdRequestChange = (val: string | undefined) => {
        if (val != null) {
            setThirdRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleSuspendedRequestChange = (val: string | undefined) => {
        if (val != null) {
            setSuspendedDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    // Reset form on close
    const handleBoxClose = () => {
        setNoteDetail('');
        setFirstRequestDate('');
        setSecondRequestDate('');
        setThirdRequestDate('');
        setNoteDate(formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY"));
        setSuspendedDate('');
        setTaxAmount('');
        setNoteDetailFiled('');
        setDateComplete('');
        setDateCompleteError('');
        setLargeReport(false);
        handleClose();
    };

    // Reset form when selectedRows changes
    useEffect(() => {
        if (!selectedRows || selectedRows.length !== 1) {
            setNoteDetail('');
            setFirstRequestDate('');
            setSecondRequestDate('');
            setThirdRequestDate('');
            setNoteDate(formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY"));
            setSuspendedDate('');
        }
    }, [selectedRows]);

    return (
        <Dialog open={open} onClose={handleBoxClose} maxWidth={'lg'}>
            <DialogTitle className='card-title'>
                {(openFiledNotes) ? "Filed Notes" : "Filer Notes"}
            </DialogTitle>
            <React.Fragment>
                <Formik
                    initialValues={initialValuesNote}
                    onSubmit={(data) => {
                        handleSubmit({
                            status: currentStatus ?? '',
                            noteDetail: noteDetail,
                            noteType: 'Select One',
                            noteUser: user?.userFirstName ?? '',
                            firstRequestDate: firstRequestDate !== "Invalid date" ? firstRequestDate : '',
                            secondRequestDate,
                            thirdRequestDate,
                            suspendedDate,
                            noteDate: noteDate !== "Invalid date" ? noteDate : '',
                            policySubStatus: '',
                            dateDeclarationFiled: '',
                            dateFinalReportSentToClient: dateComplete ?? '',
                            taxAmount: taxAmount ?? '',
                            largeReport: largeReport,
                            largeReportFee: largeReportFee ?? ''
                        });
                        handleBoxClose();
                    }}
                >
                    {({ values }) => {
                        return (
                            <Form className='main-form'>
                                <DialogContent>
                                    {(targetStatus === "No Report Required" || openFiledNotes) ? (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            disableFuture
                                                            minDate={new Date('1900-01-01')}
                                                            maxDate={new Date()}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Date Completed"
                                                            className="select-input-border text-input-box"
                                                            value={dateComplete ?? ''}
                                                            onChange={(v) => handleDateComplete(v?.toString())}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="dateComplete"
                                                                    name="dateComplete"
                                                                    error={!!dateCompleteError || !dateComplete}
                                                                    helperText={
                                                                        dateCompleteError
                                                                            ? dateCompleteError
                                                                            : !dateComplete
                                                                            ? 'Date Completed is required'
                                                                            : ''
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            {(openFiledNotes) && (
                                                <>
                                                    {/* Tax Amount Field */}
                                                    <Grid item xs={12}>
                                                        <FormControl sx={{ width: '100%', minWidth: '400px' }}>
                                                            <TextField
                                                                label="Tax Amount"
                                                                placeholder="Enter '0' for Zero Reports"
                                                                type="number"
                                                                fullWidth
                                                                variant="outlined"
                                                                sx={{ marginTop: 2 }}
                                                                value={noteDetailFiled}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    if (/^\d*\.?\d*$/.test(value)) {
                                                                        handleNoteDetailChangeFiled(e);
                                                                    }
                                                                }}
                                                                InputProps={{
                                                                    inputProps: { min: 0, step: "0.01" }
                                                                }}
                                                                error={!noteDetailFiled && openFiledNotes}
                                                                helperText={!noteDetailFiled && openFiledNotes ? 'This field is required' : ''}
                                                            />
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Large Report Field */}
                                                    <Grid item xs={12}>
                                                        <FormControl sx={{ width: '100%', minWidth: '400px', marginTop: 2 }}>
                                                            <FormLabel>Large Report</FormLabel>
                                                            <RadioGroup
                                                                row
                                                                value={largeReport}
                                                                onChange={(e) => {
                                                                    setLargeReport(e.target.value === 'true');
                                                                    // Clear the fee when setting largeReport to false
                                                                    if (e.target.value === 'false') {
                                                                        setLargeReportFee('');
                                                                    }
                                                                }}
                                                            >
                                                                <FormControlLabel value="true" control={<Radio />} label="True" />
                                                                <FormControlLabel value="false" control={<Radio />} label="False" />
                                                            </RadioGroup>
                                                            <FormHelperText error={largeReport === null && (openFiledNotes)}>
                                                                {largeReport === null && (openFiledNotes) ? 'This field is required' : ''}
                                                            </FormHelperText>
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Large Report Fee Field - only show when Large Report is true */}
                                                    {largeReport && (
                                                        <Grid item xs={12}>
                                                            <FormControl sx={{ width: '100%', minWidth: '400px', marginTop: 2 }}>
                                                                <TextField
                                                                    label="Large Report Fee"
                                                                    type="number"
                                                                    value={largeReportFee}
                                                                    onChange={(e) => setLargeReportFee(e.target.value)}
                                                                    placeholder="Enter fee amount (e.g., 50.00)"
                                                                    variant="outlined"
                                                                    fullWidth
                                                                    helperText="Enter the additional fee for large reports"
                                                                />
                                                            </FormControl>
                                                        </Grid>
                                                    )}
                                                </>
                                            )}

                                            <Grid item xs={12}>
                                                <FormControl sx={{ width: '100%', minWidth: '400px' }}>
                                                    <TextField
                                                        label="Note"
                                                        rows={3}
                                                        type="text"
                                                        fullWidth
                                                        variant="outlined"
                                                        sx={{ marginTop: 2 }}
                                                        multiline
                                                        value={noteDetail}
                                                        onChange={handleNote}
                                                        required={targetStatus === "No Report Required"}
                                                        error={!noteDetail && targetStatus === "No Report Required"}
                                                        helperText={
                                                            !noteDetail && targetStatus === "No Report Required"
                                                                ? 'Note is required'
                                                                : ''
                                                        }
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControl sx={{ width: '100%', minWidth: '500px' }}>
                                                    <FormHelperText className='formtexthelper-heading'>
                                                        Note
                                                    </FormHelperText>
                                                    <TextField
                                                        label="Note"
                                                        rows={3}
                                                        type="text"
                                                        fullWidth
                                                        variant="outlined"
                                                        sx={{ marginTop: 2 }}
                                                        multiline
                                                        value={noteDetail}
                                                        onChange={(e) => handleNoteDetailChange(e)}
                                                        required
                                                        error={!noteDetail.trim()}
                                                        helperText={!noteDetail.trim() ? 'Note is required' : ''}
                                                    />
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sx={{ marginTop: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="First Request"
                                                            className="select-input-border text-input-box"
                                                            value={firstRequestDate ?? ''}
                                                            onChange={(v) => handleFirstRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="firstRequestDate"
                                                                    name="firstRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Second Request"
                                                            className="select-input-border text-input-box"
                                                            value={secondRequestDate ?? ''}
                                                            onChange={(v) => handleSecondRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="secondRequestDate"
                                                                    name="secondRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Third & Final Request"
                                                            className="select-input-border text-input-box"
                                                            value={thirdRequestDate ?? ''}
                                                            onChange={(v) => handleThirdRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="thirdRequestDate"
                                                                    name="thirdRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={.3}></Grid>
                                                {showNoteDateField && (
                                                    <Grid item xl={3} lg={3} md={6} sm={12} xs={12} marginTop={3}>
                                                        <FormHelperText className='formtexthelper-heading'>Note Date</FormHelperText>
                                                        <DatePicker
                                                            inputFormat="MM/DD/YYYY"
                                                            label={null}
                                                            className="select-input-border text-input-box"
                                                            shouldDisableDate={shouldDisableDate}
                                                            value={noteDate}
                                                            onChange={handleNoteDate}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="effectiveDateStart"
                                                                    name="policyEffDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <button
                                        className='btn-2 btn d-flex btn-primary search-btn'
                                        type='button'
                                        onClick={handleBoxClose}
                                    >
                                        Cancel
                                    </button>
                                    {(() => {
                                        const hasPermission = user?.userRoles.split(",").includes("Super Admin") ||
                                            user?.userRoles.split(",").includes("MISC Filer") ||
                                            user?.userRoles.split(",").includes("MISC State Reporter");

                                        if (!hasPermission) {
                                            return (
                                                <button
                                                    className='btn btn-primary active-btn-blue search-btn'
                                                    disabled
                                                >
                                                    Submit
                                                </button>
                                            );
                                        }

                                        let isValidForSubmit = false;

                                        if (openFiledNotes) {
                                            isValidForSubmit = !!(dateComplete && noteDetailFiled.trim());
                                        } else {
                                            switch (targetStatus) {
                                                case "Filed":
                                                    isValidForSubmit = !!(dateComplete && noteDetailFiled);
                                                    break;
                                                case "No Report Required":
                                                    isValidForSubmit = !!(dateComplete && noteDetail.trim());
                                                    break;
                                                case "Waiting on Client":
                                                    isValidForSubmit = !!noteDetail.trim();
                                                    break;
                                                default:
                                                    isValidForSubmit = !!noteDetail.trim();
                                                    break;
                                            }
                                        }

                                        return (
                                            <button
                                                className='btn btn-primary active-btn-blue search-btn'
                                                type='submit'
                                                disabled={!isValidForSubmit}
                                            >
                                                Submit
                                            </button>
                                        );
                                    })()}
                                </DialogActions>
                            </Form>
                        );
                    }}
                </Formik>
            </React.Fragment>
        </Dialog>
    );
};

export default StateReportFilerNotes;
